import { type Character, codePoint, isInvisible, readCharacter } from "./characters";
import { readPoints, writePoints } from "./points";
import type { Mode } from "./settings";
import { spansOf, valueAt } from "./table";
import blocks from "./tables/blocks.json";
import categories from "./tables/categories.json";
import emoji from "./tables/emoji.json";

// One key of the keyboard: what typing it puts in the box, what is written on it, and what it is called. The code
// point is there for the keys that are one — a flag, a family and a keycap are several, which is the whole reason a
// key is this and not a `Character`, and why the name is carried rather than looked up for those.
export interface Key {
  text: string;
  label: string;
  name: string;
  code: number | null;
  invisible: boolean;
}

export interface Keys {
  keys: Key[];
  total: number;
  // Whether the search took anything out, which is the difference between "512 of the 20992 in it" and "3 matches",
  // and between a block with nothing to type in it and a search that found nothing in one.
  sifted: boolean;
}

// A section of the list, which is one of the two arrangements rather than anything either of them is divided into.
export interface Section {
  name: string;
  groups: string[];
}

// What somebody typed into the search, read once rather than once per candidate and read two ways at the same time:
// the letters, which is what a name is matched on, and the code point they named if they named one — `2192`,
// `U+2192`, `0x2192`, or the character itself, pasted in from wherever they found it.
export interface Search {
  raw: string;
  text: string;
  code: number | null;
}

// The keyboard is two arrangements of the standard, both Unicode's own and neither one anybody here chose. The
// blocks are where the standard puts a character, and their names are what a reader already searches for — "Arrows",
// "Currency Symbols", "Mathematical Operators". The emoji groups are the other one, and they are not a nicety: an
// emoji is scattered across ten blocks, and a flag, a family and a keycap are sequences of code points that no block
// holds at all, so a keyboard made of blocks alone can never type one. Both are generated, because a hand-written
// list of useful characters is a list nobody can predict the contents of and one that goes stale the next version.
const SPANS = new Map(spansOf(blocks).filter(({ value }) => value !== "No Block").map((span) => [span.value, span]));

// A pair per emoji — what it is and what it is called — rather than an object with two keys spelled out 1918 times.
const EMOJI: Record<string, string[][]> = emoji;

export const EMOJI_GROUPS = Object.keys(EMOJI);
export const BLOCKS = [...SPANS.keys()];

// Emoji first, since they are what most people came to type and the blocks are three hundred and forty-seven deep.
export const SECTIONS: Section[] = [
  { name: "Emoji", groups: EMOJI_GROUPS },
  { name: "Blocks", groups: BLOCKS },
];

// A search nobody has typed into, which is what a group asked for whole is asked for with.
const NO_SEARCH: Search = { raw: "", text: "", code: null };

export const DEFAULT_GROUP = "Smileys & Emotion";

// A block runs to twenty thousand characters and a grid of those is a page that stutters; the line above the keys
// says how many there were, so the cap is never mistaken for the group being that size. Every emoji group fits.
export const MAX_KEYS = 512;

export function isGroup(value: unknown): value is string {
  return typeof value === "string" && (value in EMOJI || SPANS.has(value));
}

export function keysOf(group: string, search: Search = NO_SEARCH): Keys {
  // A group whose own name is what matched shows everything in it: somebody who searched "Arrows" and then picked
  // Arrows meant the block, and not the one character in it their query happens to name as well.
  const sift = search.text !== "" && !group.toLowerCase().includes(search.text) ? search : NO_SEARCH;
  return group in EMOJI ? emojiKeys(group, sift) : blockKeys(group, sift);
}

// Unicode's own name for an emoji rather than the standard's name for the code points under it: `flag: Australia`
// is what somebody is looking for, and the two code points it is made of have no name between them at all.
function emojiKeys(group: string, search: Search): Keys {
  const found = (EMOJI[group] ?? []).filter(([text, name]) => search.text === "" || holds(text, name, search));
  return {
    keys: found.slice(0, MAX_KEYS).map(([text, name]) => ({
      text,
      label: text,
      name,
      code: [...text].length === 1 ? text.codePointAt(0) ?? null : null,
      invisible: false,
    })),
    total: found.length,
    sifted: search.text !== "",
  };
}

// Everything in the block that is a character at all: an unassigned code point is nothing to type, and half of a
// surrogate pair is not a character and cannot be put in a text. A control is left in, since a page about what is
// hiding in a text is the one page where typing one is the point.
function blockKeys(block: string, search: Search): Keys {
  const span = SPANS.get(block);
  if (!span) return { keys: [], total: 0, sifted: false };

  const keys: Key[] = [];
  let total = 0;
  for (let code = span.start; code <= span.end; code++) {
    if (!isTypable(code)) continue;
    // The one thing a block can be searched by, its characters being named in the files this page fetches rather
    // than in the chunk it is written in. A pasted character is a code point too, which is what `codeOf` reads.
    if (search.text !== "" && search.code !== code) continue;
    total++;
    // The name is left to the standard's own, which the page asks for by code point and reads a block at a time.
    if (keys.length < MAX_KEYS) keys.push(keyOf(readCharacter(code)));
  }
  return { keys, total, sifted: search.text !== "" };
}

// The line above the keys: how many are drawn against how many there were, so a cap is never mistaken for the group
// being that size, and the same bargain said in the search's own words where a search is what took the rest out.
export function keysLabel({ keys, total, sifted }: Keys): string {
  if (total > keys.length) return `${keys.length} of the ${total} ${sifted ? "that match" : "in it"}`;
  if (sifted) return `${total} match${total === 1 ? "" : "es"}`;
  return `${total} character${total === 1 ? "" : "s"}`;
}

// What the search leaves of the list. A group is kept for its own name — "Arrows", "Currency Symbols" and
// "Mathematical Operators" are what a reader already searches for — and for anything in it that matches, since
// "flags" is a group and "australia" is a character in one and nobody should have to say which they meant. A
// section with nothing left in it goes with its groups rather than staying behind as an empty heading.
export function filterSections(search: Search): Section[] {
  if (search.text === "") return SECTIONS;
  return SECTIONS
    .map(({ name, groups }) => ({
      name,
      groups: groups.filter((group) => group.toLowerCase().includes(search.text) || groupHolds(group, search)),
    }))
    .filter(({ groups }) => groups.length > 0);
}

export function readSearch(query: string): Search {
  const raw = query.trim();
  return { raw, text: raw.toLowerCase(), code: raw === "" ? null : codeOf(raw) };
}

// The code point a search names, which is either the number somebody wrote in any of the spellings this page already
// reads, or the character itself where what they had was the character and not the number under it.
function codeOf(query: string): number | null {
  const read = readPoints(query);
  const points = read.error === "" ? [...read.text] : [];
  const one = points.length === 1 ? points[0] : [...query].length === 1 ? query : "";
  return one === "" ? null : one.codePointAt(0) ?? null;
}

// Whether anything in the group is what the search names, which is asked of an emoji group by its names and of a
// block by arithmetic: a block's characters are named in the 1.4 MB of files this page fetches a name at a time, and
// holding all of them at once to search them is the one thing that split exists to avoid. So a block answers for the
// code points it covers, which is a comparison rather than a table, and an emoji answers for the name it carries.
function groupHolds(group: string, search: Search): boolean {
  const found = EMOJI[group];
  if (found) return found.some(([text, name]) => holds(text, name, search));

  const span = SPANS.get(group);
  if (!span || search.code === null) return false;
  return search.code >= span.start && search.code <= span.end && isTypable(search.code);
}

// The name for the letters somebody typed, and the characters themselves for a character they pasted — a flag being
// two code points and one key, it is found by the flag and never by a number.
function holds(text: string, name: string, search: Search): boolean {
  if (name.toLowerCase().includes(search.text)) return true;
  if (text.includes(search.raw)) return true;
  return search.code !== null && [...text].some((one) => one.codePointAt(0) === search.code);
}

// A code point somebody could put in a text: nothing has been assigned to the one, and the other is half of a pair.
function isTypable(code: number): boolean {
  const category = valueAt(categories, code);
  return category !== "Cn" && category !== "Cs";
}

function keyOf(character: Character): Key {
  return {
    text: character.text,
    label: keyLabel(character),
    name: "",
    code: character.code,
    invisible: isInvisible(character),
  };
}

// What is written on a key. A character that draws nothing wears its abbreviation, and where Unicode publishes none
// it wears its code point: eleven kinds of space are eleven identical empty keys otherwise, and the one thing that
// tells EN SPACE from THIN SPACE is the number. The name is on the key's tooltip either way.
export function keyLabel(character: Character): string {
  if (!isInvisible(character)) return character.text;
  return character.abbreviation || codePoint(character.code).slice("U+".length);
}

// What a key puts in the box, which is whatever the box is being read as: the characters themselves in one mode and
// the code points under them in the other, since a box read as code points would take a flag as a run of hex digits
// it is not. The space after them separates them from the next key, and the space before is there only where what
// the caret is sitting after would otherwise run into it.
export function typed(key: Key, mode: Mode, before: string): string {
  if (mode !== "points") return key.text;
  return `${before === "" || /\s$/.test(before) ? "" : " "}${writePoints(key.text)} `;
}
